/* One shared definition, independent state and teardown for every section instance. */
if (!customElements.get('agrolix-reels')) {
  customElements.define('agrolix-reels', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const on = (target, type, callback, options = {}) => target.addEventListener(type, callback, { ...options, signal: this.abort.signal });
      this.viewport = this.querySelector('.ar__window');
      this.track = this.querySelector('.ar__track');
      this.cards = [...this.querySelectorAll('.ar__card:not([data-copy])')];
      this.dialog = this.querySelector('dialog');
      this.pauseButton = this.querySelector('[data-pause]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.visible = false;
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches;
      this.editorSelected = false;
      this.hovered = false;
      if (!this.cards.length) return;
      this.toggleAttribute('data-single', this.cards.length === 1);
      this.querySelector('.ar__controls').hidden = this.cards.length < 2;
      this.querySelectorAll('[data-video-trigger]').forEach(button => { button.hidden = false; });
      this.setAttribute('data-ready', '');
      this.rebuild();
      this.syncPause();
      on(this, 'click', event => {
        if (this.suppressClick) { event.preventDefault(); return; }
        const button = event.target.closest('[data-next], [data-prev], [data-pause], [data-video-trigger], [data-close]');
        if (!button) return;
        if (button.hasAttribute('data-next')) this.move(1, true);
        if (button.hasAttribute('data-prev')) this.move(-1, true);
        if (button.hasAttribute('data-pause')) { this.paused = !this.paused; this.syncPause(); this.schedule(); }
        if (button.hasAttribute('data-video-trigger')) this.openVideo(button);
        if (button.hasAttribute('data-close')) this.dialog.close();
      });
      on(this.viewport, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
          if (this.moving) return;
          this.index = event.key === 'Home' ? 0 : this.cards.length - 1;
          this.place(); this.update(true); this.schedule();
        } else this.move(event.key === 'ArrowRight' ? 1 : -1, true);
      });
      on(this.viewport, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } });
      on(this.viewport, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      on(this, 'focusin', () => this.schedule());
      on(this, 'focusout', () => queueMicrotask(() => { if (this.isConnected) this.schedule(); }));
      on(document, 'visibilitychange', () => { if (document.hidden) this.dialog.querySelector('video')?.pause(); this.schedule(); });
      on(this.motion, 'change', () => { if (this.motion.matches) this.paused = true; this.syncPause(); this.schedule(); });
      on(this.viewport, 'pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        this.gesture = { x: event.clientX, y: event.clientY, id: event.pointerId };
        clearTimeout(this.timer);
      });
      on(window, 'pointerup', event => {
        const start = this.gesture;
        if (!start || start.id !== event.pointerId) return;
        this.gesture = null;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          this.suppressClick = true;
          clearTimeout(this.clickTimer);
          this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 250);
          this.move(dx < 0 ? 1 : -1, true);
        }
        this.schedule();
      });
      on(window, 'pointercancel', () => { this.gesture = null; this.schedule(); });
      on(this.viewport, 'dragstart', event => event.preventDefault());
      on(this.track, 'transitionend', event => { if (event.target === this.track && event.propertyName === 'transform') this.finish(); });
      on(this.dialog, 'click', event => { if (event.target === this.dialog) { const r = this.dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close(); } });
      on(this.dialog, 'close', () => {
        this.querySelector('.ar__player').replaceChildren();
        this.opener?.focus({ preventScroll: true });
        this.schedule();
      });
      on(document, 'shopify:block:select', event => {
        const selected = this.cards.findIndex(card => card.dataset.blockId === event.detail?.blockId);
        if (selected < 0) return;
        this.finish(); this.editorSelected = true; this.index = selected; this.place(); this.update(); this.schedule();
      });
      on(document, 'shopify:block:deselect', event => { if (this.cards.some(card => card.dataset.blockId === event.detail?.blockId)) { this.editorSelected = false; this.schedule(); } });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.setAttribute('data-reveal', '');
        this.schedule();
      }, { threshold: .1 });
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => {
        const layoutKey = [this.viewport.clientWidth, this.cards[0].getBoundingClientRect().width, getComputedStyle(this.track).gap].join(':');
        if (layoutKey !== this.layoutKey) { this.layoutKey = layoutKey; this.rebuild(); }
      });
      this.resize.observe(this.viewport);
    }
    rebuild() {
      clearTimeout(this.finishTimer);
      this.moving = false; this.removeAttribute('data-moving');
      this.track.querySelectorAll('[data-copy]').forEach(card => card.remove());
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      this.cardWidth = this.cards[0].getBoundingClientRect().width;
      this.step = this.cardWidth + gap;
      this.centerOffset = this.cards.length === 1 ? 0 : (this.viewport.clientWidth - this.cardWidth) / 2;
      if (!this.step) return;
      this.buffer = this.cards.length > 1 ? Math.ceil(this.viewport.clientWidth / this.step) + 1 : 0;
      const makeCopy = index => {
        const card = this.cards[(index % this.cards.length + this.cards.length) % this.cards.length].cloneNode(true);
        card.dataset.copy = '';
        card.removeAttribute('data-shopify-editor-block');
        card.querySelectorAll('template, [data-no-js]').forEach(node => node.remove());
        [card, ...card.querySelectorAll('[id]')].forEach(node => node.removeAttribute('id'));
        return card;
      };
      const before = document.createDocumentFragment(), after = document.createDocumentFragment();
      for (let i = -this.buffer; i < 0; i++) before.append(makeCopy(i));
      for (let i = 0; i < this.buffer; i++) after.append(makeCopy(i));
      this.track.prepend(before); this.track.append(after);
      this.place(); this.update(); this.schedule();
    }
    place() { this.track.style.transform = `translate3d(${this.centerOffset - ((this.buffer + this.index) * this.step)}px,0,0)`; }
    move(direction, announce = false) {
      if (this.cards.length < 2 || this.moving || this.dialog.open) return;
      clearTimeout(this.timer);
      this.moving = true; this.announce = announce;
      this.setAttribute('data-moving', '');
      // Flush the starting position before crossing a wrap boundary.
      void this.track.offsetWidth;
      this.index += direction; this.place();
      const duration = this.motion.matches || !this.hasAttribute('data-animate') ? 0 : Number(this.dataset.duration);
      this.finishTimer = setTimeout(() => this.finish(), duration + 60);
    }
    finish() {
      if (!this.moving) return;
      clearTimeout(this.finishTimer);
      this.moving = false; this.removeAttribute('data-moving');
      this.index = (this.index + this.cards.length) % this.cards.length;
      this.place(); this.update(this.announce); this.schedule();
    }
    update(announce = false) {
      const start = this.buffer + this.index;
      const center = start;
      const focused = document.activeElement;
      [...this.track.children].forEach((card, i) => {
        const left = (i - center) * this.step + this.centerOffset;
        const interactive = this.cards.length === 1 || (left >= -1 && left + this.cardWidth <= this.viewport.clientWidth + 1);
        if (!interactive && card.contains(focused)) this.viewport.focus({ preventScroll: true });
        // Fully visible desktop/tablet reels are interactive; partial mobile previews stay decorative.
        card.inert = !interactive;
        card.setAttribute('aria-hidden', String(!interactive));
        card.toggleAttribute('data-active', i === center);
      });
      const count = this.querySelector('[data-count]');
      count.textContent = `${this.index + 1} / ${this.cards.length}`;
      if (announce) this.querySelector('[data-status]').textContent = `Reel ${this.index + 1} of ${this.cards.length}`;
    }
    syncPause() {
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton.querySelector('span').textContent = this.paused ? 'Resume sliding' : 'Pause sliding';
      this.pauseButton.querySelector('[data-pause-icon]').hidden = this.paused;
      this.pauseButton.querySelector('[data-resume-icon]').hidden = !this.paused;
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.abort || this.paused || !this.visible || this.hovered || this.moving || this.gesture || this.dialog.open || document.hidden || this.editorSelected || this.contains(document.activeElement) || this.cards.length < 2) return;
      this.timer = setTimeout(() => this.move(1), Number(this.dataset.interval) * 1000);
    }
    openVideo(button) {
      const card = this.cards.find(item => item.dataset.blockId === button.closest('.ar__card').dataset.blockId);
      const template = card?.querySelector('template');
      if (!template) return;
      this.finish(); this.opener = button;
      this.querySelector('.ar__dialog-title').textContent = button.dataset.title;
      this.querySelector('.ar__transcript').textContent = card.dataset.transcript || '';
      const player = this.querySelector('.ar__player');
      player.replaceChildren(template.content.cloneNode(true));
      this.dialog.showModal(); this.schedule();
      const video = player.querySelector('video');
      video.addEventListener('error', () => { this.querySelector('.ar__error').hidden = false; }, { signal: this.abort.signal });
      this.querySelector('.ar__error').hidden = true;
      video.play().catch(() => { /* Native controls remain available if autoplay is blocked. */ });
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); this.resize?.disconnect();
      clearTimeout(this.timer); clearTimeout(this.finishTimer); clearTimeout(this.clickTimer);
      this.querySelector('.ar__player')?.replaceChildren();
      if (this.dialog?.open) this.dialog.close();
      this.track?.querySelectorAll('[data-copy]').forEach(card => card.remove());
      this.removeAttribute('data-ready');
    }
  });
}
